import { useEffect, useMemo, useState } from "react";
import { hasRole, isFinanceLead } from "@/lib/roles";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, ArrowLeft, Lock, CheckCircle2, Trash2, FileSpreadsheet, FileArchive, X, RotateCw, FileDown, ShieldCheck, Stamp, Calculator, ArrowRightLeft, Upload } from "lucide-react";
import BulkLoanDialog from "@/components/BulkLoanDialog";
import { useAuth } from "@/context/AuthContext";
import { MoneyInput } from "@/components/MoneyInput";

function fmt(n) {
    if (n == null) return "₦0";
    return "₦" + Number(n).toLocaleString("en-NG", { maximumFractionDigits: 0 });
}

const LEGACY_STRUCT = [
    { key: "basic_salary", label: "Basic", percent: 60, field: "basic_salary" },
    { key: "housing", label: "Housing", percent: 25, field: "housing_allowance" },
    { key: "transport", label: "Transport", percent: 15, field: "transport_allowance" },
];

// Columns follow the salary structure saved in Settings (read from the items' breakdown)
function structureColumns(items) {
    const withBreakdown = items.find((i) => (i.salary_breakdown || []).length > 0);
    if (!withBreakdown) return LEGACY_STRUCT;
    return withBreakdown.salary_breakdown.map((r) => ({ key: r.key, label: r.label || r.key, percent: r.percent }));
}

function structAmount(item, key) {
    const row = (item.salary_breakdown || []).find((r) => r.key === key);
    if (row) return row.amount;
    const legacy = LEGACY_STRUCT.find((c) => c.key === key);
    return legacy ? item[legacy.field] : 0;
}


const STATUS_PILLS = {
    draft: { label: "Draft", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
    verified: { label: "Verified", cls: "bg-purple-50 text-purple-700 border-purple-200" },
    approved: { label: "Approved", cls: "bg-blue-50 text-blue-700 border-blue-200" },
    finalized: { label: "Approved", cls: "bg-blue-50 text-blue-700 border-blue-200" },
    paid: { label: "Paid", cls: "bg-green-50 text-green-700 border-green-200" },
};

function StatusPill({ status }) {
    const s = STATUS_PILLS[status] || { label: status, cls: "bg-ink/5 text-ink/70 border-ink/10" };
    return (
        <span className={`inline-flex items-center px-2 py-1 border text-[11px] uppercase tracking-[0.15em] ${s.cls}`}>
            {s.label}
        </span>
    );
}

function periodLabel(p) {
    if (!p || p.length !== 7) return p;
    const [y, m] = p.split("-");
    const month = new Date(+y, +m - 1, 1).toLocaleDateString("en-US", { month: "long" });
    return `${month} ${y}`;
}

function TaxCalculator() {
    const [direction, setDirection] = useState("gross_to_net");
    const [amount, setAmount] = useState("");
    const [allowances, setAllowances] = useState("");
    const [annualRent, setAnnualRent] = useState("");
    const [isExpat, setIsExpat] = useState(false);
    const [isNonPayroll, setIsNonPayroll] = useState(false);
    const [result, setResult] = useState(null);
    const [calculating, setCalculating] = useState(false);

    const flip = () => {
        setDirection((d) => (d === "gross_to_net" ? "net_to_gross" : "gross_to_net"));
        setResult(null);
    };

    const calc = async (e) => {
        e?.preventDefault?.();
        const val = Number(amount);
        if (!val || val <= 0) {
            toast.error("Enter a positive amount");
            return;
        }
        setCalculating(true);
        try {
            const { data } = await api.post("/payroll/calc/tax", {
                amount: val,
                direction,
                is_expat: isExpat,
                is_non_payroll: isNonPayroll,
                allowances: Number(allowances) || 0,
                annual_rent: Number(annualRent) || 0,
            });
            setResult(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Calculation failed");
        } finally {
            setCalculating(false);
        }
    };

    const inputLabel = direction === "gross_to_net" ? "Monthly gross (₦)" : "Target monthly net (₦)";

    return (
        <div className="mt-12 bg-white border border-ink/10" data-testid="tax-calculator">
            <div className="px-6 md:px-8 py-6 border-b border-ink/10 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="overline">Quick estimate</div>
                    <h2 className="mt-2 font-display font-bold text-xl text-ink flex items-center gap-2">
                        <Calculator className="w-5 h-5 text-klein" /> Tax & Net pay calculator
                    </h2>
                    <p className="text-xs text-ink/50 mt-1">
                        Nigeria Tax Act 2026 PAYE · 60/25/15 split · 8% pension (auto-waived for expats &amp; non-payroll).
                    </p>
                </div>
                <button
                    type="button"
                    onClick={flip}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-ink/15 text-xs uppercase tracking-[0.15em] hover:bg-ink/5"
                    data-testid="calc-flip"
                    title="Switch between Gross→Net and Net→Gross"
                >
                    <ArrowRightLeft className="w-3.5 h-3.5" />
                    {direction === "gross_to_net" ? "Gross → Net" : "Net → Gross"}
                </button>
            </div>
            <form onSubmit={calc} className="px-6 md:px-8 py-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                <div className="md:col-span-4">
                    <label className="overline block mb-2">{inputLabel} *</label>
                    <MoneyInput
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                        placeholder="e.g. 1,500,000"
                        data-testid="calc-amount"
                        required
                    />
                </div>
                <div className="md:col-span-3">
                    <label className="overline block mb-2">Allowances (₦)</label>
                    <MoneyInput
                        value={allowances}
                        onChange={(e) => setAllowances(e.target.value)}
                        className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                        placeholder="0"
                        data-testid="calc-allowances"
                    />
                </div>
                <div className="md:col-span-3">
                    <label className="overline block mb-2" title="Annual rent paid — Nigeria Tax Act 2026 rent relief = min(20% of rent, ₦500,000)">
                        Annual rent (₦) <span className="text-klein normal-case">· tax relief</span>
                    </label>
                    <MoneyInput
                        value={annualRent}
                        onChange={(e) => setAnnualRent(e.target.value)}
                        className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                        placeholder="e.g. 1,200,000"
                        data-testid="calc-annual-rent"
                    />
                </div>
                <div className="md:col-span-3 flex flex-col gap-2 text-sm">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={isExpat} onChange={(e) => setIsExpat(e.target.checked)} className="accent-klein" data-testid="calc-expat" />
                        <span>Expatriate (no pension)</span>
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={isNonPayroll} onChange={(e) => setIsNonPayroll(e.target.checked)} className="accent-klein" data-testid="calc-nonpayroll" />
                        <span>Non-payroll (no PAYE / pension)</span>
                    </label>
                </div>
                <div className="md:col-span-2">
                    <button
                        type="submit"
                        disabled={calculating}
                        className="w-full inline-flex items-center justify-center gap-2 bg-klein text-white py-3 text-sm hover:bg-klein/90 disabled:opacity-60"
                        data-testid="calc-submit"
                    >
                        <Calculator className="w-4 h-4" /> {calculating ? "Calculating…" : "Calculate"}
                    </button>
                </div>
            </form>
            {result && (
                <div className="px-6 md:px-8 pb-6" data-testid="calc-result">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
                        <div className="bg-white p-4">
                            <div className="overline">Gross</div>
                            <div className="mt-1 font-display font-bold text-lg tabular-nums">{fmt(result.gross)}</div>
                        </div>
                        <div className="bg-white p-4">
                            <div className="overline">Pension</div>
                            <div className="mt-1 font-display font-bold text-lg tabular-nums">{fmt(result.pension)}</div>
                        </div>
                        <div className="bg-white p-4">
                            <div className="overline">PAYE</div>
                            <div className="mt-1 font-display font-bold text-lg tabular-nums">{fmt(result.paye)}</div>
                            <div className="text-[10px] text-ink/40 mt-0.5">{result.effective_tax_rate}% effective</div>
                        </div>
                        <div className="bg-klein text-white p-4">
                            <div className="overline !text-white/70">Net pay</div>
                            <div className="mt-1 font-display font-bold text-lg tabular-nums">{fmt(result.net)}</div>
                        </div>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-px bg-ink/10 border border-ink/10">
                        <div className="bg-white p-3">
                            <div className="overline">Basic (60%)</div>
                            <div className="mt-1 font-medium text-sm tabular-nums">{fmt(result.basic)}</div>
                        </div>
                        <div className="bg-white p-3">
                            <div className="overline">Housing (25%)</div>
                            <div className="mt-1 font-medium text-sm tabular-nums">{fmt(result.housing)}</div>
                        </div>
                        <div className="bg-white p-3">
                            <div className="overline">Transport (15%)</div>
                            <div className="mt-1 font-medium text-sm tabular-nums">{fmt(result.transport)}</div>
                        </div>
                    </div>
                    <div className="mt-3 text-xs text-ink/50 flex flex-wrap gap-x-4 gap-y-1">
                        <span>Annual gross: <strong className="text-ink">{fmt(result.annual_gross)}</strong></span>
                        <span>Annual PAYE: <strong className="text-ink">{fmt(result.annual_paye)}</strong></span>
                        {result.rent_relief > 0 && (
                            <span className="text-klein" data-testid="calc-rent-relief">
                                Rent relief: <strong>{fmt(result.rent_relief)}/yr</strong> (min of 20% rent vs ₦500K cap)
                            </span>
                        )}
                        {direction === "net_to_gross" && (
                            <span className="text-klein">Computed gross to hit your target net.</span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function PayrollList() {
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    const today = new Date();
    const defaultPeriod = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    const [form, setForm] = useState({ period: defaultPeriod, note: "" });
    const [creating, setCreating] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/payroll");
            setRuns(data);
        } catch (e) {
            toast.error("Failed to load payroll runs");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const onCreate = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const { data } = await api.post("/payroll", form);
            toast.success(`Payroll run created for ${periodLabel(data.period)} — ${data.headcount} staff`);
            setOpen(false);
            navigate(`/payroll/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Create failed");
        } finally {
            setCreating(false);
        }
    };

    const onDelete = async (run) => {
        if (!window.confirm(`Delete payroll run for ${periodLabel(run.period)}?`)) return;
        try {
            await api.delete(`/payroll/${run.id}`);
            toast.success("Payroll run deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
        }
    };

    return (
        <div className="px-8 md:px-12 py-12 md:py-16" data-testid="payroll-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Compensation</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02]">
                        Payroll
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-lg">
                        Generate monthly payroll runs from your active staff list. Edit allowances, PAYE, pension and other
                        deductions per employee, then finalize and mark as paid.
                    </p>
                </div>
                <button
                    onClick={() => { setForm({ period: defaultPeriod, note: "" }); setOpen(true); }}
                    className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 text-sm hover:bg-klein/90"
                    data-testid="add-payroll-run-button"
                >
                    <Plus className="w-4 h-4" /> New payroll run
                </button>
            </div>

            <TaxCalculator />

            <div className="mt-12 bg-white border border-ink/10 overflow-auto max-h-[420px]">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="border-b-2 border-ink/20">
                            <th className="text-left overline px-6 py-4">Period</th>
                            <th className="text-left overline px-6 py-4">Status</th>
                            <th className="text-left overline px-6 py-4 hidden md:table-cell">Headcount</th>
                            <th className="text-right overline px-6 py-4">Gross</th>
                            <th className="text-right overline px-6 py-4 hidden lg:table-cell">Deductions</th>
                            <th className="text-right overline px-6 py-4">Net pay</th>
                            <th className="text-right overline px-6 py-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="7" className="px-6 py-20 text-center text-sm text-ink/40">Loading…</td></tr>
                        ) : runs.length === 0 ? (
                            <tr><td colSpan="7" className="px-6 py-20 text-center">
                                <div className="text-ink/40 text-sm mb-3">No payroll runs yet.</div>
                                <button
                                    onClick={() => setOpen(true)}
                                    className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2 text-sm hover:bg-ink hover:text-white"
                                    data-testid="empty-add-payroll-button"
                                >
                                    <Plus className="w-4 h-4" /> Run your first payroll
                                </button>
                            </td></tr>
                        ) : (
                            runs.map((r) => {
                                const t = r.totals || {};
                                const totalDed = (t.paye || 0) + (t.pension || 0) + (t.other_deductions || 0);
                                return (
                                    <tr
                                        key={r.id}
                                        className="border-b border-ink/10 hover:bg-bone/70 cursor-pointer"
                                        data-testid={`payroll-row-${r.id}`}
                                        onClick={() => navigate(`/payroll/${r.id}`)}
                                    >
                                        <td className="px-6 py-4">
                                            <div className="font-display font-bold text-ink text-lg">{periodLabel(r.period)}</div>
                                            {r.note && <div className="text-xs text-ink/50 truncate max-w-xs">{r.note}</div>}
                                        </td>
                                        <td className="px-6 py-4"><StatusPill status={r.status} /></td>
                                        <td className="px-6 py-4 text-sm text-ink hidden md:table-cell tabular-nums">{r.headcount}</td>
                                        <td className="px-6 py-4 text-sm text-ink text-right tabular-nums">{fmt(t.gross)}</td>
                                        <td className="px-6 py-4 text-sm text-ink/70 text-right hidden lg:table-cell tabular-nums">{fmt(totalDed)}</td>
                                        <td className="px-6 py-4 font-display font-bold text-klein text-right tabular-nums">{fmt(t.net)}</td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                                {r.status !== "paid" && (
                                                    <button
                                                        onClick={() => onDelete(r)}
                                                        className="p-2 text-ink/60 hover:text-destructive hover:bg-destructive/5"
                                                        data-testid={`payroll-delete-${r.id}`}
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {open && (
                <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="payroll-create-dialog">
                    <form
                        onSubmit={onCreate}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white border border-ink/10 w-full max-w-md animate-fade-in-up"
                    >
                        <div className="flex items-center justify-between px-8 py-6 border-b border-ink/10">
                            <div>
                                <div className="overline">New run</div>
                                <h3 className="mt-1 font-display font-bold text-xl">Create payroll run</h3>
                            </div>
                            <button type="button" onClick={() => setOpen(false)} className="p-2 text-ink/60 hover:bg-ink/5">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="px-8 py-6 space-y-5">
                            <div>
                                <label className="overline block mb-2">Pay period (YYYY-MM) *</label>
                                <input
                                    required
                                    type="month"
                                    value={form.period}
                                    onChange={(e) => setForm({ ...form, period: e.target.value })}
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                    data-testid="payroll-period-input"
                                />
                                <p className="text-xs text-ink/50 mt-2">
                                    A line item will be auto-generated for every active employee with their current salary
                                    and an 8% pension deduction. You can edit each line before finalizing.
                                </p>
                            </div>
                            <div>
                                <label className="overline block mb-2">Note (optional)</label>
                                <input
                                    value={form.note}
                                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                                    placeholder="e.g. April 2026 monthly run"
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                    data-testid="payroll-note-input"
                                />
                            </div>
                        </div>
                        <div className="px-8 py-5 border-t border-ink/10 flex items-center justify-end gap-3">
                            <button type="button" onClick={() => setOpen(false)} className="px-5 py-2.5 border border-ink/20 text-sm hover:bg-ink/5">
                                Cancel
                            </button>
                            <button type="submit" disabled={creating} className="px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="payroll-create-button">
                                {creating ? "Generating…" : "Generate run"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

export function PayrollDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isFinLead = isFinanceLead(user);
    const [run, setRun] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/payroll/${id}`);
            setRun(data);
        } catch (err) {
            toast.error("Payroll run not found");
            navigate("/payroll");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const isLocked = run && (run.status === "approved" || run.status === "finalized" || run.status === "paid");

    const updateField = async (itemId, key, value) => {
        const numeric = value === "" ? 0 : Number(value) || 0;
        try {
            const { data } = await api.put(`/payroll/items/${itemId}`, { [key]: numeric });
            setRun((r) => {
                if (!r) return r;
                const items = r.items.map((i) => (i.id === itemId ? { ...i, ...data } : i));
                const totals = items.reduce(
                    (acc, i) => ({
                        gross: acc.gross + (i.gross_salary || 0),
                        allowances: acc.allowances + (i.allowances || 0),
                        paye: acc.paye + (i.paye || 0),
                        pension: acc.pension + (i.pension || 0),
                        loan_deduction: acc.loan_deduction + (i.loan_deduction || 0),
                        other_deductions: acc.other_deductions + (i.other_deductions || 0),
                        net: acc.net + (i.net_pay || 0),
                    }),
                    { gross: 0, allowances: 0, paye: 0, pension: 0, loan_deduction: 0, other_deductions: 0, net: 0 }
                );
                return { ...r, items, totals };
            });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
        }
    };

    const setStatus = async (status) => {
        const confirmMsg = {
            verified: "Mark this run as verified? Items can no longer be edited.",
            approved: "Approve this payroll run? It will be ready for payment.",
            paid: "Mark this run as paid? This will deduct loan installments scheduled for this period.",
            draft: "Send back to draft for editing?",
        }[status] || "Update status?";
        if (!window.confirm(confirmMsg)) return;
        try {
            const { data } = await api.put(`/payroll/${id}`, { status });
            setRun(data);
            toast.success(`Run ${status}`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
        }
    };

    const recalcPaye = async () => {
        if (!window.confirm("Recalculate PAYE & pension for every line using the Nigerian PIT formula?")) return;
        try {
            const { data } = await api.post(`/payroll/${id}/recalc-paye`);
            setRun(data);
            toast.success("PAYE & pension recalculated");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Recalc failed");
        }
    };

    const downloadPayslip = async (item) => {
        try {
            const res = await api.get(`/payroll/${id}/payslip/${item.id}`, { responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `payslip_${run.period}_${(item.employee_name || "employee").replace(/\s+/g, "_")}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("Could not download payslip");
        }
    };

    const downloadBankAdvice = async () => {
        try {
            const res = await api.get(`/payroll/${id}/bank-advice`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `bank_advice_payroll_${run.period}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Bank advice downloaded");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Bank advice download failed");
        }
    };

    const [downloadingZip, setDownloadingZip] = useState(false);
    const [bulkLoanOpen, setBulkLoanOpen] = useState(false);
    const downloadAllPayslips = async () => {
        if (downloadingZip) return;
        setDownloadingZip(true);
        try {
            const res = await api.get(`/payroll/${id}/payslips.zip`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `payslips_${run.period}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Payslips ZIP downloaded");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Bulk payslip download failed");
        } finally {
            setDownloadingZip(false);
        }
    };

    const deleteRun = async () => {
        if (!window.confirm(`Permanently delete this ${run.status} payroll run? Any loan/advance installments deducted in this period will be rolled back.`)) return;
        try {
            await api.delete(`/payroll/${id}`);
            toast.success("Run deleted");
            navigate("/payroll");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
        }
    };

    const filtered = useMemo(() => {
        if (!run) return [];
        const q = filter.trim().toLowerCase();
        if (!q) return run.items;
        return run.items.filter((i) =>
            (i.employee_name || "").toLowerCase().includes(q) ||
            (i.employee_no || "").toLowerCase().includes(q) ||
            (i.department || "").toLowerCase().includes(q) ||
            (i.division || "").toLowerCase().includes(q)
        );
    }, [run, filter]);

    const structCols = useMemo(() => structureColumns(run?.items || []), [run]);

    if (loading || !run) {
        return <div className="px-8 md:px-12 py-12 text-sm text-ink/40">Loading payroll…</div>;
    }
    const t = run.totals || {};
    const totalDed = (t.paye || 0) + (t.pension || 0) + (t.loan_deduction || 0) + (t.other_deductions || 0);

    return (
        <div className="px-8 md:px-12 py-12 md:py-16" data-testid="payroll-detail-page">
            <button
                onClick={() => navigate("/payroll")}
                className="inline-flex items-center gap-2 text-sm text-ink/60 hover:text-klein mb-8"
                data-testid="back-to-payroll"
            >
                <ArrowLeft className="w-4 h-4" /> All payroll runs
            </button>

            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline flex items-center gap-3">
                        <span>Pay period</span>
                        <StatusPill status={run.status} />
                    </div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02]">
                        {periodLabel(run.period)}
                    </h1>
                    {run.note && <p className="mt-2 text-sm text-ink/60">{run.note}</p>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {run.status === "draft" && (hasRole(user, "admin", "hr")) && (
                        <button
                            onClick={recalcPaye}
                            className="inline-flex items-center gap-2 border border-ink/20 px-4 py-3 text-sm hover:bg-ink/5"
                            data-testid="recalc-paye-button"
                            title="Recalculate PAYE & pension using Nigerian PIT bands"
                        >
                            <RotateCw className="w-4 h-4" /> Recalc PAYE
                        </button>
                    )}
                    {run.status === "draft" && (hasRole(user, "admin", "hr", "accountant")) && (
                        <button
                            onClick={() => setStatus("verified")}
                            className="inline-flex items-center gap-2 bg-purple-600 text-white px-5 py-3 text-sm hover:bg-purple-700"
                            data-testid="verify-button"
                        >
                            <ShieldCheck className="w-4 h-4" /> Verify
                        </button>
                    )}
                    {run.status === "verified" && (hasRole(user, "admin", "hr", "accountant")) && (
                        <button
                            onClick={() => setStatus("draft")}
                            className="inline-flex items-center gap-2 border border-ink/20 px-4 py-3 text-sm hover:bg-ink/5"
                            data-testid="unverify-button"
                            title="Send back to draft"
                        >
                            Unverify
                        </button>
                    )}
                    {run.status === "verified" && (hasRole(user, "admin") || isFinLead) && (
                        <button
                            onClick={() => setStatus("approved")}
                            className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 text-sm hover:bg-klein/90"
                            data-testid="approve-button"
                        >
                            <Stamp className="w-4 h-4" /> Approve
                        </button>
                    )}
                    {(run.status === "approved" || run.status === "finalized" || run.status === "paid") && (hasRole(user, "admin", "hr", "accountant")) && (
                        <button
                            onClick={downloadBankAdvice}
                            className="inline-flex items-center gap-2 border border-klein text-klein px-4 py-3 text-sm hover:bg-klein hover:text-white"
                            data-testid="bank-advice-button"
                            title="Download bank advice CSV — one row per employee for batch disbursement"
                        >
                            <FileSpreadsheet className="w-4 h-4" /> Bank advice
                        </button>
                    )}
                    {(hasRole(user, "admin", "hr", "accountant", "disbursement")) && (
                        <button
                            onClick={downloadAllPayslips}
                            disabled={downloadingZip}
                            className="inline-flex items-center gap-2 border border-ink/20 text-ink px-4 py-3 text-sm hover:bg-ink hover:text-white disabled:opacity-50"
                            data-testid="bulk-payslips-zip-button"
                            title="Download every employee's payslip as a single ZIP"
                        >
                            <FileArchive className="w-4 h-4" />
                            {downloadingZip ? "Zipping…" : "All payslips (.zip)"}
                        </button>
                    )}
                    {(hasRole(user, "admin", "hr")) && (
                        <button
                            onClick={() => setBulkLoanOpen(true)}
                            className="inline-flex items-center gap-2 border border-klein text-klein px-4 py-3 text-sm hover:bg-klein hover:text-white"
                            data-testid="bulk-loans-button"
                            title="Bulk-upload loans / advances via Excel — auto-applies to this run when auto-approved"
                        >
                            <Upload className="w-4 h-4" />
                            Bulk loans / advances
                        </button>
                    )}
                    {(run.status === "approved" || run.status === "finalized") && (hasRole(user, "admin", "hr")) && (
                        <button
                            onClick={() => setStatus("paid")}
                            className="inline-flex items-center gap-2 bg-green-600 text-white px-5 py-3 text-sm hover:bg-green-700"
                            data-testid="mark-paid-button"
                        >
                            <CheckCircle2 className="w-4 h-4" /> Mark as paid
                        </button>
                    )}
                    {hasRole(user, "admin") && (
                        <button
                            onClick={deleteRun}
                            className="inline-flex items-center gap-2 border border-destructive/30 text-destructive px-4 py-3 text-sm hover:bg-destructive hover:text-white"
                            data-testid="delete-run-button"
                            title="Permanently delete this payroll run"
                        >
                            <Trash2 className="w-4 h-4" /> Delete
                        </button>
                    )}
                </div>
            </div>

            {/* Totals */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
                <div className="bg-white p-6" data-testid="totals-headcount">
                    <div className="overline">Headcount</div>
                    <div className="mt-3 font-display font-black text-3xl text-ink tabular-nums">{run.headcount}</div>
                </div>
                <div className="bg-white p-6" data-testid="totals-gross">
                    <div className="overline">Gross</div>
                    <div className="mt-3 font-display font-black text-3xl text-ink tabular-nums truncate" title={fmt((t.gross || 0) + (t.allowances || 0))}>{fmt((t.gross || 0) + (t.allowances || 0))}</div>
                    {(t.allowances || 0) > 0 && (
                        <div className="text-xs text-ink/50 mt-1" data-testid="totals-other-allowance">incl. {fmt(t.allowances)} non-taxable other allowance</div>
                    )}
                </div>
                <div className="bg-white p-6" data-testid="totals-deductions">
                    <div className="overline">Total deductions</div>
                    <div className="mt-3 font-display font-black text-3xl text-ink/80 tabular-nums truncate" title={fmt(totalDed)}>{fmt(totalDed)}</div>
                    <div className="text-xs text-ink/50 mt-1">PAYE + Pension + Loan + other</div>
                </div>
                <div className="bg-klein text-white p-6" data-testid="totals-net">
                    <div className="overline !text-white/70">Net pay</div>
                    <div className="mt-3 font-display font-black text-3xl tabular-nums truncate" title={fmt(t.net)}>{fmt(t.net)}</div>
                </div>
            </div>

            {/* Search */}
            <div className="mt-8 bg-white border border-ink/10 p-4">
                <input
                    placeholder="Search name, staff no, department, division, phone…"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="w-full border border-ink/10 px-3 py-2.5 text-sm focus:border-klein focus:outline-none"
                    data-testid="payroll-search-input"
                />
            </div>

            {/* Items */}
            <div className="mt-4 bg-white border border-ink/10 overflow-auto max-h-[520px]" data-testid="payroll-items-table">
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="border-b-2 border-ink/20 bg-bone">
                            <th className="text-left overline px-3 py-3">Employee</th>
                            <th className="text-left overline px-3 py-3 hidden xl:table-cell">Dept</th>
                            {structCols.map((c) => (
                                <th key={c.key} className="text-right overline px-3 py-3 whitespace-nowrap" data-testid={`payroll-col-${c.key}`}>
                                    {c.label.replace(/ allowance$/i, "")}<span className="text-ink/40 font-normal normal-case">&nbsp;{c.percent}%</span>
                                </th>
                            ))}
                            <th className="text-right overline px-3 py-3 whitespace-nowrap" title="Other allowance — non-taxable, paid on top of gross">Other allow.</th>
                            <th className="text-right overline px-3 py-3 hidden lg:table-cell">Absent</th>
                            <th className="text-right overline px-3 py-3">PAYE</th>
                            <th className="text-right overline px-3 py-3">Pension</th>
                            <th className="text-right overline px-3 py-3">Loan</th>
                            <th className="text-right overline px-3 py-3">Advance</th>
                            <th className="text-right overline px-3 py-3 hidden md:table-cell">Other</th>
                            <th className="text-right overline px-3 py-3 bg-klein text-white">Net</th>
                            <th className="text-right overline px-3 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && (
                            <tr><td colSpan={20} className="px-4 py-10 text-center text-sm text-ink/40" data-testid="payroll-items-empty">No staff match this search</td></tr>
                        )}
                        {filtered.map((i) => (
                            <tr key={i.id} className="border-b border-ink/10 hover:bg-bone/40" data-testid={`payroll-item-${i.id}`}>
                                <td className="px-3 py-3 min-w-[220px]">
                                    <div className="text-ink font-medium truncate">{i.employee_name}</div>
                                    <div className="text-xs text-ink/50 truncate">
                                        {i.employee_no || "—"} {i.department ? `· ${i.department}` : ""}
                                    </div>
                                </td>
                                <td className="px-3 py-3 text-ink/70 hidden xl:table-cell">{i.department || "—"}</td>
                                {structCols.map((c) => (
                                    <td key={c.key} className="px-3 py-3 text-right text-ink tabular-nums" data-testid={`item-${i.id}-struct-${c.key}`}>
                                        {fmt(structAmount(i, c.key))}
                                    </td>
                                ))}
                                <td className="px-3 py-3 text-right text-ink tabular-nums" data-testid={`item-${i.id}-other-allowance`}>
                                    {(i.allowances || 0) > 0 ? fmt(i.allowances) : <span className="text-ink/30">—</span>}
                                </td>
                                <td className="px-3 py-3 text-right hidden lg:table-cell">
                                    {(i.absent_days || 0) > 0 ? (
                                        <div>
                                            <div className="tabular-nums text-red-700">{fmt(i.absent_deduction)}</div>
                                            <div className="text-[10px] text-ink/50 leading-tight">{i.absent_days} day{i.absent_days !== 1 ? "s" : ""}</div>
                                        </div>
                                    ) : (
                                        <span className="text-ink/30">—</span>
                                    )}
                                </td>
                                <td className="px-3 py-3 text-right">
                                    <NumInput
                                        value={i.paye}
                                        onChange={(v) => updateField(i.id, "paye", v)}
                                        disabled={true}
                                        testid={`item-${i.id}-paye`}
                                    />
                                </td>
                                <td className="px-3 py-3 text-right">
                                    <NumInput
                                        value={i.pension}
                                        onChange={(v) => updateField(i.id, "pension", v)}
                                        disabled={true}
                                        testid={`item-${i.id}-pension`}
                                    />
                                </td>
                                <td className="px-3 py-3 text-right">
                                    {(() => {
                                        const loanRefs = (i.loan_refs || []).filter((r) => (r.type || "loan") === "loan");
                                        const loanAmt = loanRefs.reduce((s, r) => s + (r.amount || 0), 0);
                                        return (
                                            <div className="flex items-center justify-end gap-1">
                                                <span className="tabular-nums">{fmt(loanAmt)}</span>
                                                {loanRefs.length > 0 && (
                                                    <span className="text-[10px] uppercase tracking-[0.1em] text-klein bg-klein/10 px-1.5 py-0.5">auto</span>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </td>
                                <td className="px-3 py-3 text-right">
                                    {(() => {
                                        const advRefs = (i.loan_refs || []).filter((r) => r.type === "advance");
                                        const advAmt = advRefs.reduce((s, r) => s + (r.amount || 0), 0);
                                        return (
                                            <div className="flex items-center justify-end gap-1">
                                                <span className="tabular-nums">{fmt(advAmt)}</span>
                                                {advRefs.length > 0 && (
                                                    <span className="text-[10px] uppercase tracking-[0.1em] text-amber-700 bg-amber-50 px-1.5 py-0.5">auto</span>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </td>
                                <td className="px-3 py-3 text-right hidden md:table-cell">
                                    <NumInput
                                        value={i.other_deductions}
                                        onChange={(v) => updateField(i.id, "other_deductions", v)}
                                        disabled={isLocked}
                                        testid={`item-${i.id}-other`}
                                    />
                                </td>
                                <td className="px-3 py-3 text-right font-display font-bold text-klein tabular-nums">
                                    {fmt(i.net_pay)}
                                </td>
                                <td className="px-3 py-3 text-right">
                                    <button
                                        type="button"
                                        onClick={() => downloadPayslip(i)}
                                        className="inline-flex items-center gap-1 text-xs text-klein hover:text-ink"
                                        data-testid={`item-${i.id}-payslip`}
                                        title="Download payslip PDF"
                                    >
                                        <FileDown className="w-3.5 h-3.5" /> PDF
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {isLocked && (
                <div className="mt-6 border border-ink/10 bg-bone p-4 text-sm text-ink/70 flex items-center gap-3">
                    <Lock className="w-4 h-4 text-ink/50" />
                    This run is {run.status}. Items can no longer be edited.
                </div>
            )}

            {bulkLoanOpen && (
                <BulkLoanDialog
                    canAutoApprove={hasRole(user, "admin", "hr")}
                    onClose={() => setBulkLoanOpen(false)}
                    onDone={async (data) => {
                        try {
                            // Apply the freshly-created loans/advances to THIS open run
                            // so the deductions reflect immediately. Only meaningful when
                            // some rows were auto-approved (otherwise they sit as pending).
                            if (data?.auto_approved && data?.created_count > 0 && !isLocked) {
                                const { data: refreshed } = await api.post(`/payroll/${run.id}/apply-loans`);
                                if ((refreshed?.items_updated || 0) > 0) {
                                    toast.success(`Applied to ${refreshed.items_updated} payslip${refreshed.items_updated === 1 ? "" : "s"} on this run.`);
                                }
                            }
                        } catch (err) {
                            toast.error(formatApiError(err.response?.data?.detail) || "Could not apply loans to this run");
                        }
                        await load();
                        setBulkLoanOpen(false);
                    }}
                />
            )}
        </div>
    );
}

function NumInput({ value, onChange, disabled, testid }) {
    const [local, setLocal] = useState(String(value ?? 0));
    useEffect(() => { setLocal(String(value ?? 0)); }, [value]);
    return (
        <MoneyInput
            wrapperClassName="w-28"
            disabled={disabled}
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            onBlur={() => {
                if (Number(local) !== Number(value)) onChange(local);
            }}
            className="w-full border border-ink/15 px-2 py-1.5 text-sm text-right tabular-nums focus:border-klein focus:outline-none disabled:bg-ink/5 disabled:text-ink/60"
            data-testid={testid}
        />
    );
}
